import { useRef, useState, useCallback } from "react";
import { format } from "date-fns";
import html2canvas from "html2canvas-pro";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { RankTrackerReport } from "@/components/rank-tracker/RankTrackerReport";
import { useAppData } from "@/contexts/AppDataContext";

export default function ReportPreview() {
  const reportRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const { brands, selectedBrandId } = useAppData();

  const handleExport = useCallback(async () => {
    if (!reportRef.current) {
      toast.error("Report not ready, try again");
      return;
    }
    setIsExporting(true);
    try {
      console.log("[PDF] start, ref exists:", !!reportRef.current);
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        width: element.scrollWidth,
        height: element.scrollHeight,
      });
      console.log("[PDF] canvas:", canvas.width, canvas.height);
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas is empty (0x0)");
      }

      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const pxPerMm = canvas.width / pageWidthMm;
      const pageHeightPx = Math.floor(pageHeightMm * pxPerMm);

      let srcY = 0;
      let pageNum = 0;
      while (srcY < canvas.height) {
        const sliceHeightPx = Math.min(pageHeightPx, canvas.height - srcY);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext("2d");
        if (!ctx) throw new Error("Could not get canvas context");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, -srcY);
        const sliceDataUrl = sliceCanvas.toDataURL("image/jpeg", 0.92);
        const sliceHeightMm = (sliceHeightPx / canvas.width) * pageWidthMm;
        if (pageNum > 0) pdf.addPage();
        pdf.addImage(sliceDataUrl, "JPEG", 0, 0, pageWidthMm, sliceHeightMm);
        srcY += pageHeightPx;
        pageNum += 1;
      }

      console.log("[PDF] pages:", pageNum);
      const selectedBrand = brands.find((b) => b.id === selectedBrandId);
      const brandSlug = selectedBrand
        ? selectedBrand.name.toLowerCase().replace(/\s+/g, "-")
        : "brand";
      const dateStr = format(new Date(), "yyyy-MM-dd");
      const filename = `rank-tracker-${brandSlug}-${dateStr}.pdf`;
      pdf.save(filename);
      console.log("[PDF] saved");
    } catch (error) {
      console.error("[PDF export] error:", error);
      toast.error(`Export failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsExporting(false);
    }
  }, [brands, selectedBrandId]);

  return (
    <RankTrackerReport
      onExport={handleExport}
      isExporting={isExporting}
      reportRef={reportRef}
    />
  );
}
